'use client';

import React, { useEffect, useState } from 'react';
import { Users, BadgeCheck, Briefcase, AlertTriangle, TrendingUp, TrendingDown, UserPlus, CreditCard, CheckCircle,  } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface KpiCard {
  id: string;
  label: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral' | 'warning' | 'danger';
  icon: React.ElementType;
  href: string;
  span?: 'col-2' | 'default';
  alert?: boolean;
  heroCard?: boolean;
  subtext?: string;
}

const changeColors = {
  positive: 'text-emerald-600',
  negative: 'text-red-600',
  neutral: 'text-muted-foreground',
  warning: 'text-amber-600',
  danger: 'text-red-700',
};

export default function DashboardBentoGrid() {
  const [kpiCards, setKpiCards] = useState<KpiCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchKpis() {
      const supabase = createClient();

      const [
        { count: totalArtisans = 0 } = {},
        { count: pendingVerifications = 0 } = {},
        { count: overdueVerifications = 0 } = {},
        { count: activeJobs = 0 } = {},
        { count: openDisputes = 0 } = {},
        { count: escalatedDisputes = 0 } = {},
        { data: revenueData = [] } = {},
        { data: newSignups = [] } = {},
        { count: completedJobs = 0 } = {},
        { count: totalJobs = 0 } = {},
        { count: newSchemaArtisans = 0 } = {},
        { count: newSchemaCustomers = 0 } = {},
        { count: totalOrganizations = 0 } = {},
        { count: totalBookings = 0 } = {},
      ] = await Promise.all([
        // Legacy artisan count
        supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'artisan')
          .eq('status', 'active'),
        supabase
          .from('artisan_verifications')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'under_review'),
        supabase
          .from('artisan_verifications')
          .select('*', { count: 'exact', head: true })
          .eq('is_overdue', true)
          .eq('status', 'under_review'),
        supabase
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .in('status', ['posted', 'assigned', 'inprogress']),
        supabase
          .from('disputes')
          .select('*', { count: 'exact', head: true })
          .in('status', ['open', 'mediation', 'escalated']),
        supabase
          .from('disputes')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'escalated'),
        supabase
          .from('platform_stats')
          .select('revenue')
          .order('stat_date', { ascending: false })
          .limit(30),
        supabase
          .from('platform_stats')
          .select('new_artisans, new_customers')
          .order('stat_date', { ascending: false })
          .limit(7),
        supabase
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'completed'),
        supabase
          .from('jobs')
          .select('*', { count: 'exact', head: true })
          .not('status', 'eq', 'posted'),
        // New schema: artisan_profile count
        supabase
          .from('artisan_profile')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active'),
        // New schema: customer_profile count
        supabase
          .from('customer_profile')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active'),
        // New schema: organization_profile count
        supabase
          .from('organization_profile')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true),
        // New schema: bookings count
        supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .in('status', ['pending', 'confirmed', 'in_progress']),
      ]);

      const totalRevenue = revenueData?.reduce((s, r) => s + (r.revenue ?? 0), 0) ?? 0;
      const weekArtisans = newSignups?.reduce((s, r) => s + (r.new_artisans ?? 0), 0) ?? 0;
      const weekCustomers = newSignups?.reduce((s, r) => s + (r.new_customers ?? 0), 0) ?? 0;
      const completionRate =
        (totalJobs ?? 0) > 0
          ? (((completedJobs ?? 0) / (totalJobs ?? 1)) * 100).toFixed(1)
          : '0.0';

      // Use new schema counts if available, otherwise fall back to legacy
      const effectiveArtisans = (newSchemaArtisans ?? 0) > 0 ? (newSchemaArtisans ?? 0) : (totalArtisans ?? 0);
      const effectiveActiveJobs = (totalBookings ?? 0) > 0 ? (totalBookings ?? 0) : (activeJobs ?? 0);

      const cards: KpiCard[] = [
        {
          id: 'kpi-revenue',
          label: 'Platform Revenue (OMR)',
          value: `OMR ${totalRevenue.toLocaleString()}`,
          change: 'Last 30 days · Commission earned',
          changeType: 'positive',
          icon: CreditCard,
          href: '/dashboard',
          span: 'col-2',
          heroCard: true,
          subtext: 'Commission earned',
        },
        {
          id: 'kpi-artisans',
          label: 'Active Artisans',
          value: effectiveArtisans.toLocaleString(),
          change: `+${weekArtisans} this week`,
          changeType: 'positive',
          icon: Users,
          href: '/user-management',
        },
        {
          id: 'kpi-verifications',
          label: 'Pending Verifications',
          value: String(pendingVerifications ?? 0),
          change: `${overdueVerifications ?? 0} awaiting > 48h`,
          changeType: 'warning',
          icon: BadgeCheck,
          href: '/artisan-verification-queue',
          alert: true,
          subtext: 'Needs review',
        },
        {
          id: 'kpi-jobs',
          label: 'Active Jobs & Bookings',
          value: effectiveActiveJobs.toLocaleString(),
          change: 'Posted, assigned & in progress',
          changeType: 'positive',
          icon: Briefcase,
          href: '/jobs-orders',
        },
        {
          id: 'kpi-disputes',
          label: 'Open Disputes',
          value: String(openDisputes ?? 0),
          change: `${escalatedDisputes ?? 0} escalated`,
          changeType: escalatedDisputes && escalatedDisputes > 0 ? 'danger' : 'warning',
          icon: AlertTriangle,
          href: '/dispute-management',
          alert: (openDisputes ?? 0) > 0,
          subtext: escalatedDisputes && escalatedDisputes > 0 ? 'Urgent attention' : undefined,
        },
        {
          id: 'kpi-completion',
          label: 'Job Completion Rate',
          value: `${completionRate}%`,
          change: 'All-time completion rate',
          changeType: parseFloat(completionRate) >= 80 ? 'positive' : 'negative',
          icon: CheckCircle,
          href: '/jobs-orders',
        },
        {
          id: 'kpi-signups',
          label: 'New Signups This Week',
          value: String(weekArtisans + weekCustomers),
          change: `${weekArtisans} artisans · ${weekCustomers} customers`,
          changeType: 'positive',
          icon: UserPlus,
          href: '/user-management',
        },
      ];

      setKpiCards(cards);
      setLoading(false);
    }

    fetchKpis();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={`skel-${i}`}
            className={`card-elevated rounded-xl p-5 animate-pulse bg-muted h-28 ${i === 0 ? 'sm:col-span-2 lg:col-span-2' : ''}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {kpiCards.map((card) => {
        const Icon = card.icon;
        const isAlert = card.alert;
        const alertType = card.changeType === 'danger' ? 'danger' : 'warning';

        return (
          <Link
            key={card.id}
            href={card.href}
            className={`card-elevated rounded-xl p-5 flex flex-col gap-3 transition-all duration-200 hover:shadow-elevated hover:-translate-y-0.5 cursor-pointer ${
              card.span === 'col-2' ? 'sm:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2' : ''
            } ${
              isAlert
                ? alertType === 'danger' ?'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200' :'bg-card'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p
                  className={`text-xs font-600 uppercase tracking-widest ${
                    isAlert
                      ? alertType === 'danger' ?'text-red-600' :'text-amber-700' :'text-muted-foreground'
                  }`}
                >
                  {card.label}
                </p>
                {card.subtext && (
                  <p
                    className={`text-xs mt-0.5 ${
                      isAlert
                        ? alertType === 'danger' ?'text-red-500' :'text-amber-600' :'text-muted-foreground'
                    }`}
                  >
                    {card.subtext}
                  </p>
                )}
              </div>
              <div
                className={`p-2.5 rounded-lg ${
                  card.heroCard
                    ? 'bg-primary/10'
                    : isAlert
                    ? alertType === 'danger' ?'bg-red-100' :'bg-amber-100' :'bg-muted'
                }`}
              >
                <Icon
                  size={18}
                  className={
                    card.heroCard
                      ? 'text-primary'
                      : isAlert
                      ? alertType === 'danger' ?'text-red-600' :'text-amber-700' :'text-muted-foreground'
                  }
                />
              </div>
            </div>

            <div>
              <p
                className={`font-tabular font-700 text-foreground ${
                  card.heroCard ? 'text-3xl' : 'text-2xl'
                }`}
              >
                {card.value}
              </p>
            </div>

            <div className="flex items-center gap-1.5">
              {card.changeType === 'positive' && (
                <TrendingUp size={13} className="text-emerald-500 shrink-0" />
              )}
              {card.changeType === 'negative' && (
                <TrendingDown size={13} className="text-red-500 shrink-0" />
              )}
              {(card.changeType === 'warning' || card.changeType === 'danger') && (
                <AlertTriangle size={13} className="text-amber-500 shrink-0" />
              )}
              <p className={`text-xs font-500 ${changeColors[card.changeType]}`}>
                {card.change}
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}